import { useState, useCallback } from 'react';
import { login, logout } from '../api/auth';
import TokenManager from '../utils/tokenManager';

// useAuth hook: handles signin/signout without automatic logout timers
export function useAuth() {
  const [user, setUser] = useState(() => {
    const userData = TokenManager.getUserData();
    return userData;
  });

  // Internal signout logic that clears storage - defined early to avoid hoisting issues
  // Now async so callers can await completion (useful for showing a spinner)
  const doSignout = useCallback(async () => {
    try {
      await logout();
    } catch (e) {
      // If server revoke fails, still clear client state. Log the error for debugging.
      console.error('Logout/revoke failed', e);
    } finally {
      TokenManager.clearAuthData();
      setUser(null);
    }
    // Note: Components using this hook should handle navigation to login when user becomes null
  }, []);

  // Check if user has a token (don't validate expiration - let backend handle it)
  const checkTokenValidity = useCallback(() => {
    if (!user?.token) return { isValid: false, token: null };

    // Always return valid if token exists - let backend validate expiration
    return { isValid: true, token: user.token };
  }, [user]);

  // Public signin: save token/user
  const signin = async (credentials) => {
    const res = await login(credentials);
    const token = res?.data?.data?.accessToken; // Updated to match API response
    const userData = res?.data?.data?.user;

    if (token) {
      localStorage.setItem('token', token);

      if (userData) {
        const { id, email, role } = userData;
        const username = email ? email.split('@')[0] : '';
        localStorage.setItem('userId', id);
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userRole', role);
        localStorage.setItem('userUsername', username);

        const newUser = { token, id, email, role, username };
        setUser(newUser);

        // Small delay to ensure state is updated before navigation
        await new Promise((resolve) => setTimeout(resolve, 100));
      } else {
        setUser({ token });

        // Small delay to ensure state is updated before navigation
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } else {
      throw new Error('Login failed: No token received');
    }
  };

  // Public signout: call internal doSignout and return a promise so callers can await
  const signout = async () => {
    return doSignout();
  };

  return {
    user,
    signin,
    signout,
    checkTokenValidity // Expose for API usage (no automatic logout)
  };
}
